import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { message } from 'antd'
import { Select } from '@mantine/core'
import { useGetActiveLeadSourceDetails } from '../../hooks'
import { useOrganizationUsers } from '@forwardflow/hooks/customHooks/organization'
import CampaignService from '@forwardflow/services/CampaignService'
import Icon from '@forwardflow/ui/Icons'

export const AgentAssignmentConfig = () => {
   const { title } = useParams()
   const { data: LeadSource, mutate } = useGetActiveLeadSourceDetails(
      title as string
   )

   const { data: usersResponse } = useOrganizationUsers({
      page: 1,
      limit: 100,
      invitationStatus: 'accepted',
      isWhiteListed: 'true',
   })

   const whitelistedAgents = useMemo(() => {
      if (!usersResponse?.data) return []
      return (usersResponse.data as any[])
         .filter((u) => u.crmUserId)
         .map((u) => ({
            crmUserId: u.crmUserId as number,
            name: u.name || `${u.firstName} ${u.lastName}`,
            email: u.email,
            phone: u.phone,
         }))
   }, [usersResponse])

   const config = LeadSource?.data?.agentAssignmentConfig
   const [strategy, setStrategy] = useState<string>('round_robin')
   const [selectedAgentIds, setSelectedAgentIds] = useState<number[]>([])
   const [showAgentPicker, setShowAgentPicker] = useState(false)

   useEffect(() => {
      if (config) {
         setStrategy(config.strategy ?? 'round_robin')
         setSelectedAgentIds(config.agentIds ?? [])
      }
   }, [config])

   const selectedAgents = useMemo(() => {
      return selectedAgentIds
         .map((id) => whitelistedAgents.find((a) => a.crmUserId === id))
         .filter(Boolean)
   }, [selectedAgentIds, whitelistedAgents])

   const availableAgents = useMemo(() => {
      return whitelistedAgents.filter(
         (a) => !selectedAgentIds.includes(a.crmUserId)
      )
   }, [whitelistedAgents, selectedAgentIds])

   const saveConfig = async (agentIds: number[], strat?: string) => {
      try {
         await CampaignService.updateAgentAssignmentConfig(title as string, {
            enabled: true,
            strategy: (strat || strategy) as 'round_robin' | 'jump_ball',
            agentIds,
         })
         mutate()
      } catch (error: any) {
         message.error(
            error?.response?.data?.message || 'Failed to update config'
         )
      }
   }

   const handleAddAgent = (crmUserId: string | null) => {
      if (!crmUserId) return
      const id = Number(crmUserId)
      if (!selectedAgentIds.includes(id)) {
         const updated = [...selectedAgentIds, id]
         setSelectedAgentIds(updated)
         saveConfig(updated)
      }
   }

   const handleRemoveAgent = (crmUserId: number) => {
      const updated = selectedAgentIds.filter((id) => id !== crmUserId)
      if (strategy === 'jump_ball' && updated.length === 0) {
         message.error(
            'Jump Ball requires at least one agent. Switch to Round Robin first to remove all agents.'
         )
         return
      }
      setSelectedAgentIds(updated)
      saveConfig(updated)
   }

   const handleStrategyChange = (val: string | null) => {
      const newStrategy = val || 'round_robin'
      if (newStrategy === 'jump_ball' && selectedAgentIds.length === 0) {
         message.error(
            'Select at least one agent before switching to Jump Ball.'
         )
         return
      }
      setStrategy(newStrategy)
      saveConfig(selectedAgentIds, newStrategy)
   }

   return (
      <div className="flex flex-col flex-1">
         <div className="flex items-center justify-between py-3 pl-10 pr-4">
            <span className="font-medium">Agent Assignment</span>
         </div>

         <div className="mb-1 bg-primary/10">
            <div className="flex items-center px-10 py-5 font-medium bg-primary/10">
               <Icon name="Agent" className="mr-4 fill-white" />
               <span>
                  {selectedAgentIds.length > 0
                     ? `${selectedAgentIds.length} Agents`
                     : 'All Whitelisted Agents'}
               </span>
            </div>
            {selectedAgentIds.length === 0 && (
               <p className="text-[12px] text-[#B98BE6]/70 px-10 pb-4">
                  By default, all whitelisted agents are eligible and leads are
                  assigned using round robin (least busy). Add specific agents
                  below to restrict assignment to only those agents.
               </p>
            )}
            <div className="flex items-center justify-between px-10 py-4">
               <p className="text-[12px] text-[#B98BE6]/70">
                  Assignment Strategy
               </p>
               <Select
                  data={[
                     { value: 'round_robin', label: 'Round Robin (Least Busy)' },
                     { value: 'jump_ball', label: 'Jump Ball (Conference)' },
                  ]}
                  value={strategy}
                  onChange={handleStrategyChange}
                  className="w-64"
                  styles={{
                     input: {
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderColor: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        height: '36px',
                        minHeight: '36px',
                     },
                     dropdown: {
                        backgroundColor: '#1a1a2e',
                        borderColor: 'rgba(255,255,255,0.1)',
                     },
                     option: {
                        color: 'white',
                        '&[data-selected]': { backgroundColor: 'rgba(144,97,218,0.3)' },
                        '&[data-hovered]': { backgroundColor: 'rgba(255,255,255,0.05)' },
                     },
                  }}
               />
            </div>
         </div>

         <div className="flex flex-col gap-1">
            {selectedAgents.map((agent: any) => (
               <div
                  key={agent.crmUserId}
                  className="flex items-center gap-4 px-10 py-4 transition-all bg-primary/10 hover:bg-primary/20 group"
               >
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/30 shrink-0">
                     <Icon name="Person" className="fill-white scale-75" />
                  </div>
                  <div className="flex-1">
                     <p className="font-medium">{agent.name}</p>
                     <p className="text-[12px] text-white/50">
                        {agent.email}
                        {agent.phone ? ` | ${agent.phone}` : ''}
                     </p>
                  </div>
                  <button
                     onClick={() => handleRemoveAgent(agent.crmUserId)}
                     className="hidden h-10 px-4 font-medium transition-all border rounded group-hover:block border-[#C11574] bg-[#C11574]/20 hover:bg-[#C11574]/40"
                  >
                     Remove
                  </button>
               </div>
            ))}

            <div
               className="flex items-center gap-4 px-10 py-4 transition-all bg-primary/10 hover:bg-primary/20 cursor-pointer"
               onClick={() => setShowAgentPicker(!showAgentPicker)}
            >
               <div className="flex items-center justify-center w-10 h-10 rounded-full border-2 border-dashed border-white/20 shrink-0">
                  <span className="text-xl text-white/40">+</span>
               </div>
               <p className="text-sm text-white/50">Add agent to campaign</p>
            </div>

            {showAgentPicker && (
               <div className="px-10 py-3 bg-primary/10">
                  <Select
                     data={availableAgents.map((a) => ({
                        value: String(a.crmUserId),
                        label: a.name,
                     }))}
                     placeholder="Search and select agent..."
                     searchable
                     onChange={handleAddAgent}
                     value={null}
                     nothingFoundMessage="No agents available"
                     styles={{
                        input: {
                           backgroundColor: 'rgba(255,255,255,0.05)',
                           borderColor: 'rgba(255,255,255,0.1)',
                           color: 'white',
                        },
                        dropdown: {
                           backgroundColor: '#1a1a2e',
                           borderColor: 'rgba(255,255,255,0.1)',
                        },
                        option: {
                           color: 'white',
                           '&[data-hovered]': { backgroundColor: 'rgba(255,255,255,0.05)' },
                        },
                     }}
                  />
               </div>
            )}
         </div>
      </div>
   )
}
